import React, { useMemo, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { Outlet, useLocation, useNavigate, useParams, useLoaderData } from "react-router";
import { OsLayout } from "@/components/ui/os-layout";
import { SidebarLayout } from "@/components/ui/sidebar-layout/SidebarLayout";
import { OutageBanner } from "@/components/ui/outage-banner";
import { LocalDevMongoBanner } from "@/components/dev/LocalDevMongoBanner";
import { OpenworkUserTestingBanner } from "@/components/review/OpenworkUserTestingBanner";
import { CompanyDemoOsBar } from "@/components/review/CompanyDemoOsBar";
import { useCompanyDemoBarEnabled } from "@/components/review/useCompanyDemoBarEnabled";
import { RouteTransition } from "../components/RouteTransition";
import { Navigation } from "../components/navigation/Navigation";
import { WebSocketProvider } from "../context/WebSocketContext";
import { MissionControlWebSocketProvider } from "../context/MissionControlWebSocketContext";
import { GuardiansAlertNotificationListener } from "../components/guardians/GuardiansAlertNotificationListener";
import {
  ProtectedRoute,
  ProtectedShellBackdrop,
} from "../components/auth/ProtectedRoute";
import { withPermission } from "../components/auth/withPermission";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";

import { FileUploadProvider } from "@/context/FileUploadContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { APIProvider } from "@/context/APIContext";
import { CompanyProvider } from "@/context/CompanyContext";
import { InboxProvider } from "@/context/InboxContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { AniProvider, type AniChatWidgetContextValue } from "@/context/AniContext";
import { TaskProgressDialogProvider } from "@/context/TaskProgressDialogContext";
import {
  type Theme,
  LOCAL_STORAGE_THEME_KEY,
  getInitialTheme,
  mergeThemes,
} from "@/config/themes";
import { useCompanyColors } from "@/hooks/useCompanyColors";
import { RouteChangeTracker } from "../components/RouteChangeTracker";
import { installConsoleFocusFilter, wsLog } from "@/utils/debug";
import { getRouteToModuleMap } from "@/config/modules";
import { agenticHomeRouteOptions, getDefaultRoute } from "@/utils/routeUtils";
import {
  getOutageBannerData,
  type OutageBannerData,
} from "@/utils/outageBanner.server";

// Initialize profiler bridge in development mode
if (import.meta.env.DEV) {
  import("@/utils/profiler/bridge").then((m) => m.initProfilerBridge());
}

// Get WebSocket URL from environment or use default
const getWebSocketUrl = () => {
  // Use environment variable if provided
  if (import.meta.env.VITE_WEBSOCKET_URL) {
    const envUrl = import.meta.env.VITE_WEBSOCKET_URL;

    // If the URL starts with ws:// and we're on HTTPS, convert to wss://
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      envUrl.startsWith("ws://")
    ) {
      const secureUrl = envUrl.replace("ws://", "wss://");
      return secureUrl;
    }
    return envUrl;
  }

  // Otherwise, build URL based on current protocol and host
  const protocol =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "wss:"
      : "ws:";
  const host =
    typeof window !== "undefined" ? window.location.host : "app.ani.tech";
  const url = `${protocol}//${host}/ws`;
  return url;
};

const websocketUrl = getWebSocketUrl();

// Install optional console filter and gate URL log for clarity
installConsoleFocusFilter();
wsLog("WebSocket URL configured as:", websocketUrl);

// Get route to module mapping from centralized registry
const routeModules = getRouteToModuleMap();

// Define route to chat context mapping
const routeChatContexts: Record<string, AniChatWidgetContextValue> = {
  pod: "pod",
  worker: "worker",
  documents: "documents",
  "cash-flow": "cash-flow",
  "scenario-tracker": "scenarios",
  dashboard: "dashboard",
  portfolio: "portfolio",
  product: "product",
  screener: "screener",
  playground: "playground",
  "report-builder": "report-builder",
  "case-checking": "case-checking",
};

// Memoize static components
const MemoizedNavigation = React.memo(Navigation);
const NavbarPlaceholder = React.memo(() => <div className="h-16" />);

// Define props for BaseContent
interface BaseContentProps {
  companyId: string;
}

// Component that needs to be inside CompanyProvider to use useCompanyColors
const BaseContent: React.FC<BaseContentProps> = ({ companyId }) => {
  const { platformOutageBanner } = useLoaderData<{
    platformOutageBanner: OutageBannerData | null;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const {
    permissions,
    isLoading: isPermissionsLoading,
    hasPermission,
    companyLayoutSettings,
  } = useModulePermissions();
  const searchParams = new URLSearchParams(location.search);
  const hasNavigated = useRef(false);

  // Check if user has permission for OS mode
  const hasOsModePermission = hasPermission("ani_os");

  const canToggleLayout =
    hasOsModePermission &&
    !companyLayoutSettings.forceAniOs &&
    !companyLayoutSettings.disableClassicMode;

  // Layout preference state - only allow OS layout if user has permission
  const [useOsLayout, setUseOsLayout] = React.useState<boolean>(() => {
    // If company forces AniOS, always use OS layout
    if (companyLayoutSettings.forceAniOs) {
      return true;
    }
    if (typeof window !== "undefined") {
      const savedPref = localStorage.getItem("ani_layout_mode");
      // OS unless explicitly saved as classic (aligns with AuthRedirects)
      return savedPref !== "classic";
    }
    return true; // Default to OS layout
  });

  // Force OS layout when company settings require it
  React.useEffect(() => {
    if (!companyLayoutSettings.forceAniOs) return;
    if (typeof window !== "undefined") {
      localStorage.setItem("ani_layout_mode", "os");
    }
    if (!useOsLayout) {
      setUseOsLayout(true);
    }
  }, [companyLayoutSettings.forceAniOs, useOsLayout]);

  /**
   * While permissions load, `hasPermission("ani_os")` is false, which used to make
   * `isOsLayout` false on the outlet context and trigger classic-only redirects (e.g. HomeRoute).
   * If the user prefers aniOS, treat the shell as OS until loading finishes.
   */
  const effectiveUseOsLayout = React.useMemo(() => {
    if (companyLayoutSettings.forceAniOs) return true;
    if (!useOsLayout) return false;
    if (isPermissionsLoading) return true;
    return hasOsModePermission;
  }, [
    companyLayoutSettings.forceAniOs,
    useOsLayout,
    isPermissionsLoading,
    hasOsModePermission,
  ]);

  // Save layout preference to localStorage (only if user has permission and not forced)
  const toggleLayout = React.useCallback(() => {
    if (!canToggleLayout) return;

    const switchingToClassic = effectiveUseOsLayout;

    flushSync(() => {
      setUseOsLayout(!switchingToClassic);
      if (typeof window !== "undefined") {
        localStorage.setItem(
          "ani_layout_mode",
          switchingToClassic ? "classic" : "os",
        );
      }
    });

    if (typeof window === "undefined") return;

    if (switchingToClassic) {
      // Classic layout has no index route at "/" — land on the user's default module.
      const path = window.location.pathname;
      if (path === "/" || path === "") {
        const targetRoute = getDefaultRoute(
          permissions?.initial_route,
          hasPermission,
          agenticHomeRouteOptions(companyLayoutSettings.forceAniOs),
        );
        if (targetRoute && targetRoute !== "/") {
          navigate(targetRoute, { replace: true });
        }
      }
      return;
    }

    // aniOS restores workspace windows from "/" — sync URL when leaving a classic deep link.
    if (window.location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [
    canToggleLayout,
    effectiveUseOsLayout,
    permissions?.initial_route,
    hasPermission,
    companyLayoutSettings.forceAniOs,
    navigate,
  ]);

  // Get company color preferences (now inside CompanyProvider)
  const { themes: companyThemes, workspaceThemesExclusive } = useCompanyColors();
  const availableThemes = mergeThemes(companyThemes, workspaceThemesExclusive);

  // --- Theme State ---
  const [currentTheme, setCurrentTheme] = React.useState<Theme>(
    getInitialTheme(availableThemes)
  );

  React.useEffect(() => {
    if (typeof window !== "undefined" && currentTheme) {
      localStorage.setItem(LOCAL_STORAGE_THEME_KEY, currentTheme.name);
    }
  }, [currentTheme]);

  // Update currentTheme when availableThemes change
  React.useEffect(() => {
    if (availableThemes.length > 0) {
      const savedThemeName =
        typeof window !== "undefined"
          ? localStorage.getItem(LOCAL_STORAGE_THEME_KEY)
          : null;

      if (savedThemeName) {
        const foundTheme = availableThemes.find(
          (theme) => theme.name === savedThemeName
        );
        if (foundTheme) {
          setCurrentTheme(foundTheme);
          return;
        }
      }

      // If no saved theme or saved theme not found, use first available
      setCurrentTheme(availableThemes[0]);
    }
  }, [availableThemes]);
  // --- End of Theme State ---

  useEffect(() => {
    if (isAuthLoading || isPermissionsLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!permissions) {
      return;
    }

    // Prevent multiple navigations
    if (hasNavigated.current) {
      return;
    }

    // Handle single app mode redirect
    if (permissions.single_app && permissions.modules.length > 0) {
      const firstModule = permissions.modules[0].replace(/_/g, "-");
      const currentPath = window.location.pathname;

      if (!currentPath.startsWith(`/${firstModule}`)) {
        hasNavigated.current = true;
        navigate(`/${firstModule}`, { replace: true });
      }
      return;
    }

    // Handle multi-app mode - root path navigation
    if (window.location.pathname === "/") {
      hasNavigated.current = true;

      // OS Mode: Don't navigate - let OS show desktop with saved workspace windows
      if (effectiveUseOsLayout) {
        // OsProvider will restore windows from workspace config
        // Just mark as handled and stay on root
        return;
      }

      // Classic Mode: Smart fallback to first accessible route
      const targetRoute = getDefaultRoute(
        permissions.initial_route,
        hasPermission,
        agenticHomeRouteOptions(companyLayoutSettings.forceAniOs),
      );
      if (targetRoute && targetRoute !== "/") {
        navigate(targetRoute, { replace: true });
      }
      // If targetRoute is "/", user has no accessible modules - stay on root
    }
  }, [
    isAuthenticated,
    isAuthLoading,
    isPermissionsLoading,
    permissions,
    effectiveUseOsLayout,
    hasPermission,
    navigate,
  ]);

  // Get the current route's module
  const currentModule = useMemo(() => {
    const path = location.pathname.split("/")[1]; // Get first segment after /
    return routeModules[path] || null;
  }, [location.pathname]);

  // Get the current route's chat context
  const chatContext = useMemo(() => {
    const path = location.pathname.split("/")[1];
    return routeChatContexts[path];
  }, [location.pathname]);

  const outletContext = useMemo(
    () => ({
      currentTheme,
      onSelectTheme: (theme: Theme) => setCurrentTheme(theme),
      /** True when Base uses aniOS shell (matches child routes that may not mount inside OsProvider). */
      isOsLayout: effectiveUseOsLayout,
    }),
    [currentTheme, effectiveUseOsLayout]
  );

  // Create a component that just renders Outlet
  const OutletComponent = () => <Outlet context={outletContext} />;

  // Wrap the Outlet with permissions if needed
  const WrappedOutlet = useMemo(() => {
    if (!currentModule) return <Outlet context={outletContext} />;
    const PermissionedOutlet = withPermission(OutletComponent, currentModule);
    return <PermissionedOutlet />;
  }, [currentModule, outletContext]);

  // Determine if current route should use column layout
  const hasColumns = useMemo(() => {
    return (
      location.state?.layout === "columns" ||
      searchParams.get("layout") === "columns"
    );
  }, [location.state?.layout, location.search]);

  // Memoize the icons to prevent unnecessary re-renders
  const icons = useMemo(
    () => ({
      openIcon: <FontAwesomeIcon icon={faBars} />,
      closeIcon: <FontAwesomeIcon icon={faXmark} />,
    }),
    []
  );

  const aniInitialConfig = useMemo(
    () => ({
      resourceId: id,
      chatContext: chatContext,
      // apiData and onApiAction can be set later by specific components if needed
    }),
    [id, chatContext]
  );

  const showOpenworkSandboxBanner = companyId === "openworks";
  const showCompanyDemoBar = useCompanyDemoBarEnabled();

  const workspaceBanner =
    platformOutageBanner || showOpenworkSandboxBanner || showCompanyDemoBar ? (
      <div className="flex flex-col gap-2">
        {platformOutageBanner ? (
          <OutageBanner
            variant="bar"
            message={platformOutageBanner.message}
            statusUrl={platformOutageBanner.statusUrl}
            data-testid="platform-outage-banner"
          />
        ) : null}
        {showOpenworkSandboxBanner ? <OpenworkUserTestingBanner /> : null}
        {showCompanyDemoBar ? <CompanyDemoOsBar /> : null}
      </div>
    ) : null;

  return (
    <APIProvider
      cacheTTL={5 * 60 * 1000}
      debug={import.meta?.env?.DEV || false}
    >
      <RouteChangeTracker />
      <FileUploadProvider>
        <WebSocketProvider
          url={websocketUrl}
          autoConnect={false}
          debug={true}
          reconnectDelayMs={2000}
          pingIntervalMs={30000}
        >
          <MissionControlWebSocketProvider>
          <NotificationProvider>
          {!effectiveUseOsLayout ? <GuardiansAlertNotificationListener /> : null}
            <TaskProgressDialogProvider>
              <InboxProvider>
                <AniProvider initialConfig={aniInitialConfig}>
                  {effectiveUseOsLayout ? (
                    <OsLayout
                      workspaceBanner={workspaceBanner}
                      navbar={<NavbarPlaceholder />}
                      sidebar={
                        <MemoizedNavigation
                          availableThemes={availableThemes}
                          currentTheme={currentTheme}
                          onSelectTheme={(theme: Theme) => setCurrentTheme(theme)}
                          closeIcon={icons.closeIcon}
                          companyId={companyId}
                          onToggleLayout={canToggleLayout ? toggleLayout : undefined}
                          isOsShell
                        />
                      }
                      openIcon={icons.openIcon}
                      closeIcon={icons.closeIcon}
                      hasColumns={hasColumns}
                      companyId={companyId}
                      currentTheme={currentTheme}
                      availableThemes={availableThemes}
                      onSelectTheme={(theme: Theme) => setCurrentTheme(theme)}
                      onToggleLayout={canToggleLayout ? toggleLayout : undefined}
                    />
                  ) : (
                    <SidebarLayout
                      workspaceBanner={workspaceBanner}
                      navbar={<NavbarPlaceholder />}
                      sidebar={
                        <MemoizedNavigation
                          availableThemes={availableThemes}
                          currentTheme={currentTheme}
                          onSelectTheme={(theme: Theme) => setCurrentTheme(theme)}
                          closeIcon={icons.closeIcon}
                          companyId={companyId}
                          onToggleLayout={canToggleLayout ? toggleLayout : undefined}
                        />
                      }
                      openIcon={icons.openIcon}
                      closeIcon={icons.closeIcon}
                      hasColumns={hasColumns}
                      companyId={companyId}
                      currentTheme={currentTheme}
                    >
                      <RouteTransition mode="parent">{WrappedOutlet}</RouteTransition>
                    </SidebarLayout>
                  )}
                </AniProvider>
              </InboxProvider>
            </TaskProgressDialogProvider>
          </NotificationProvider>
          </MissionControlWebSocketProvider>
        </WebSocketProvider>
      </FileUploadProvider>
      <LocalDevMongoBanner />
    </APIProvider>
  );
};

const BaseInternalGate = () => {
  const { user, isLoading: isAuthLoading, isAuthenticated } = useAuth();

  if (isAuthLoading) {
    return (
      <ProtectedShellBackdrop
        testId="base-internal-auth-loading"
        ariaLabel="Loading authentication"
      />
    );
  }

  // ProtectedRoute should handle redirection if not authenticated.
  // This check is a safeguard.
  if (!isAuthenticated) {
    return null;
  }

  // At this point, authentication is not loading, and the user is authenticated.
  // Now, we check if the user object and company_id are populated.
  // This data might take a moment longer to be available.
  if (!user || !user.company_id) {
    return (
      <ProtectedShellBackdrop
        testId="base-account-bootstrap"
        ariaLabel="Loading account"
      />
    );
  }

  const companyId = user.company_id;

  return (
    <CompanyProvider companyId={companyId}>
      <BaseContent companyId={companyId} />
    </CompanyProvider>
  );
};

const Base = () => {
  return (
    <ProtectedRoute>
      <BaseInternalGate />
    </ProtectedRoute>
  );
};

export async function loader() {
  return {
    platformOutageBanner: getOutageBannerData("platform"),
  };
}

export default Base;
